import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Download, TrendingUp, TrendingDown, Activity, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface AnalyticsData {
  date: string;
  executions: number;
  success: number;
  failed: number;
  avgDuration: number;
}

interface ExecutionStats {
  name: string;
  value: number;
  color: string;
}

const MOCK_ANALYTICS_DATA: AnalyticsData[] = [
  { date: 'Mon', executions: 12, success: 10, failed: 2, avgDuration: 1.2 },
  { date: 'Tue', executions: 19, success: 15, failed: 4, avgDuration: 1.5 },
  { date: 'Wed', executions: 15, success: 12, failed: 3, avgDuration: 1.1 },
  { date: 'Thu', executions: 25, success: 23, failed: 2, avgDuration: 0.9 },
  { date: 'Fri', executions: 22, success: 20, failed: 2, avgDuration: 1.3 },
  { date: 'Sat', executions: 8, success: 7, failed: 1, avgDuration: 1.4 },
  { date: 'Sun', executions: 5, success: 5, failed: 0, avgDuration: 0.8 },
];

const EXECUTION_STATS: ExecutionStats[] = [
  { name: 'Success', value: 92, color: '#10b981' },
  { name: 'Failed', value: 8, color: '#ef4444' },
];

const NODE_PERFORMANCE = [
  { name: 'HTTP Request', avgTime: 234, executions: 156 },
  { name: 'Transform Data', avgTime: 89, executions: 142 },
  { name: 'Conditional', avgTime: 12, executions: 156 },
  { name: 'Log Output', avgTime: 5, executions: 156 },
  { name: 'Store Data', avgTime: 156, executions: 98 },
];

export function WorkflowAnalytics() {
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'year'>('week');

  const totalExecutions = MOCK_ANALYTICS_DATA.reduce((sum, d) => sum + d.executions, 0);
  const totalSuccess = MOCK_ANALYTICS_DATA.reduce((sum, d) => sum + d.success, 0);
  const totalFailed = MOCK_ANALYTICS_DATA.reduce((sum, d) => sum + d.failed, 0);
  const avgDuration = (MOCK_ANALYTICS_DATA.reduce((sum, d) => sum + d.avgDuration, 0) / MOCK_ANALYTICS_DATA.length).toFixed(2);
  const successRate = ((totalSuccess / totalExecutions) * 100).toFixed(1);

  const handleExportReport = () => {
    const report = `
WORKFLOW ANALYTICS REPORT
Generated: ${new Date().toLocaleString()}
Date Range: ${dateRange}

SUMMARY
-------
Total Executions: ${totalExecutions}
Successful: ${totalSuccess}
Failed: ${totalFailed}
Success Rate: ${successRate}%
Average Duration: ${avgDuration}s

DAILY BREAKDOWN
${MOCK_ANALYTICS_DATA.map((d) => `${d.date}: ${d.executions} executions (${d.success} success, ${d.failed} failed)`).join('\n')}

NODE PERFORMANCE
${NODE_PERFORMANCE.map((n) => `${n.name}: ${n.avgTime}ms avg (${n.executions} executions)`).join('\n')}
    `.trim();

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflow-analytics-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground mb-2">Workflow Analytics</h2>
          <p className="text-muted-foreground">Detailed insights and performance metrics</p>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-2">
            {(['week', 'month', 'year'] as const).map((range) => (
              <Button
                key={range}
                variant={dateRange === range ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDateRange(range)}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </Button>
            ))}
          </div>
          <Button onClick={handleExportReport}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Total Executions</p>
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{totalExecutions}</p>
          <p className="text-xs text-muted-foreground">This {dateRange}</p>
        </Card>

        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Success Rate</p>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{successRate}%</p>
          <Badge className="w-fit bg-green-500/10 text-green-700 border-green-500/20">
            {totalSuccess} successful
          </Badge>
        </Card>

        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Failed Executions</p>
            <TrendingDown className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{totalFailed}</p>
          <Badge className="w-fit bg-red-500/10 text-red-700 border-red-500/20">
            {((totalFailed / totalExecutions) * 100).toFixed(1)}% failure rate
          </Badge>
        </Card>

        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Avg Duration</p>
            <Activity className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{avgDuration}s</p>
          <p className="text-xs text-muted-foreground">Per execution</p>
        </Card>
      </div>

      <Tabs defaultValue="execution" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="execution">Execution Trend</TabsTrigger>
          <TabsTrigger value="success">Success Rate</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="nodes">Node Metrics</TabsTrigger>
        </TabsList>

        {/* Execution Trend */}
        <TabsContent value="execution">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Execution Trend</h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={MOCK_ANALYTICS_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="executions"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                  name="Total Executions"
                />
                <Line
                  type="monotone"
                  dataKey="success"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981' }}
                  name="Successful"
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ fill: '#ef4444' }}
                  name="Failed"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        {/* Success Rate */}
        <TabsContent value="success">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Success vs Failure</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={EXECUTION_STATS}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {EXECUTION_STATS.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Detailed Stats</h3>
              <div className="space-y-3">
                {EXECUTION_STATS.map((stat) => (
                  <div key={stat.name} className="flex items-center justify-between p-3 rounded border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stat.color }} />
                      <p className="font-medium text-foreground">{stat.name}</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">{stat.value}%</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Performance */}
        <TabsContent value="performance">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Average Execution Duration</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={MOCK_ANALYTICS_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                  }}
                />
                <Bar dataKey="avgDuration" fill="#f59e0b" name="Avg Duration (seconds)" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        {/* Node Metrics */}
        <TabsContent value="nodes">
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Node Performance</h3>
            <div className="space-y-3">
              {NODE_PERFORMANCE.map((node) => (
                <div key={node.name} className="p-4 rounded border border-border space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-foreground">{node.name}</p>
                    <Badge variant="outline">{node.executions} executions</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-600 rounded transition-all"
                        style={{ width: `${(node.avgTime / 250) * 100}%` }}
                      />
                    </div>
                    <p className="text-sm font-medium text-foreground w-16 text-right">
                      {node.avgTime}ms
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tips */}
      <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-blue-500/5 border-blue-500/20">
        <h3 className="font-semibold text-foreground mb-3">Analytics Tips</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Monitor success rates to identify problematic workflows</li>
          <li>• Optimize slow-running nodes for better performance</li>
          <li>• Use trends to identify peak execution times</li>
          <li>• Export reports for compliance and documentation</li>
          <li>• Set up alerts for failure rate spikes</li>
        </ul>
      </Card>
    </div>
  );
}
